import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Actions as ORGANISATIONACTIONS } from '../../store/actions/organisationActions';
import { Actions as AUTHENTICATION_ACTIONS } from '../../store/actions/authenticationActions';
import { Actions as PAYMENTACTIONS } from '../../store/actions/paymentActions';
import { delay } from '../utils/GlobalFunctions';

const MAX_POLL_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 2000;
const attachedSessionKey = (sessionId) => `cyf.attachedPaymentMethod.${sessionId}`;

/**
 * After Stripe Checkout success: attach payment method, poll until org is active (webhook lag), then enter app.
 */
const SubscriptionSuccessful = ({
  branding,
  loading,
  processing,
  sendUserOrderForm,
  attachPaymentMethodToSubscription,
  stripeSubscriptions,
  getOrganisation,
  setPlanUpgradeSelection,
}) => {
  const { primaryColor } = branding.colors;
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');

  const history = useHistory();
  const [pollMessage, setPollMessage] = useState('');
  const attachedSessionRef = useRef(null);
  const hasSubscriptions = Array.isArray(stripeSubscriptions) && stripeSubscriptions.length > 0;

  const sendOrderForm = async (orgId) => {
    try {
      loading(true);
      setPollMessage('Confirming your subscription…');
      for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
        const org = await getOrganisation();
        const status = org?.subscriptionStatus;
        const active = org?.hasSubscriptionActive === true;
        if (status === 'active' || (active && status !== 'trialing' && status !== 'expired')) break;
        setPollMessage(`Confirming your subscription… (${i + 1}/${MAX_POLL_ATTEMPTS})`);
        await delay(POLL_INTERVAL_MS);
      }
      await sendUserOrderForm(orgId);
      setPlanUpgradeSelection?.(null);
      history.push('/');
      window.location.reload();
    } catch {
      loading(false);
    }
  };

  // Attach once per Checkout session. Do not depend on the subscriptions array
  // identity — getOrganisation / getSubscription replace it on every poll.
  useEffect(() => {
    if (!sessionId || !hasSubscriptions) return;
    if (attachedSessionRef.current === sessionId) return;
    if (sessionStorage.getItem(attachedSessionKey(sessionId))) {
      attachedSessionRef.current = sessionId;
      return;
    }
    attachedSessionRef.current = sessionId;
    sessionStorage.setItem(attachedSessionKey(sessionId), '1');
    attachPaymentMethodToSubscription(sessionId);
  }, [sessionId, hasSubscriptions, attachPaymentMethodToSubscription]);

  const orgId = localStorage.getItem('Organisation');

  return (
    <div className='modal' style={{ display: 'block', background: 'rgba(0,0,0,.8)' }}>
      <div className='modal-dialog' style={{ height: '90vh', overflow: 'auto', fontSize: '12px', margin: '100px auto' }}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h4 className='modal-title'>Congratulations!</h4>
          </div>

          <div className='modal-body' style={{ textAlign: 'center', marginTop: '1em' }}>
            <h4>Your subscription is confirmed!</h4>
            {pollMessage && <p style={{ marginTop: '1em', color: '#666' }}>{pollMessage}</p>}

            <div className='row block buttons' style={{ marginTop: '2em', display: 'flex', justifyContent: 'center' }}>
              <button
                disabled={processing ? true : false}
                className='w-full rounded-lg bg-primary px-4 py-3 text-primary-foreground font-semibold succesful-subscription'
                id='Subscription-button'
                onClick={async () => await sendOrderForm(orgId)}
                style={{ width: processing ? '45%' : '40%', backgroundColor: primaryColor, color: 'white' }}>
                {processing && <Loader2 size={16} strokeWidth={1.5} className='animate-spin icon-spinner' />}
                Take me to my account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { stripeSubscriptions } = state?.payment?.stripe || {};
  return {
    bacsPayment: window.location.pathname.includes('success'),
    branding: state.branding,
    processing: state.authentication.processing,
    stripeSubscriptions,
  };
};

const mapDispatchToProps = (dispatch) => ({
  loading: (saving) => dispatch(AUTHENTICATION_ACTIONS.loggingIn(saving)),
  toggleModal: (status, item) => dispatch(ORGANISATIONACTIONS.toggleModal(status, item)),
  showSubscriptionModal: (status) => dispatch(PAYMENTACTIONS.showSubscriptionModal(status)),
  sendUserOrderForm: (orgId) => dispatch(PAYMENTACTIONS.sendUserOrderForm(orgId)),
  attachPaymentMethodToSubscription: (sessionId) => dispatch(PAYMENTACTIONS.attachPaymentMethodToSubscription(sessionId)),
  getOrganisation: () => dispatch(ORGANISATIONACTIONS.getOrganisation()),
  setPlanUpgradeSelection: (selection) => dispatch(PAYMENTACTIONS.setPlanUpgradeSelection(selection)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionSuccessful);
